import { inject, Injectable } from '@angular/core';
import { selectAccountHasHrPlus } from '@app/reducers/account/account.selector';
import { Store } from '@ngrx/store';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { SbDialogService } from '@sb/ui/dialog';
import { filter, take, tap } from 'rxjs';

import { AiTemplateBuilderModalComponent } from '../ai-template-builder/builder-modal/ai-template-builder-modal.component';
import { TemplateCategory } from '../models/contract-template.model';
import { TemplateTypeSelectionModalComponent } from '../preview-template-contract/template-type-selection-modal/template-type-selection-modal.component';
import { UploadTemplateContractModalComponent } from '../preview-template-contract/upload-template-contract-modal/upload-template-contract-modal.component';
import { ContractTemplateUploadDialog } from './contract-template-upload-dialog';

@Injectable({ providedIn: 'root' })
export class TemplateDialogService implements ContractTemplateUploadDialog {
  #dialogService = inject(SbDialogService);
  #featureService = inject(FeatureService);
  #hasHrPlus = inject(Store).selectSignal(selectAccountHasHrPlus);

  openUpload(data?: { category?: TemplateCategory }): void {
    this.#openUploadDialog(data);
  }

  openManualUpload(data?: { category?: TemplateCategory }): void {
    this.#openManualDialog(data);
  }

  openAddTemplateDialog() {
    if (this.#featureService.isEnabled(featureFlag.TMP_TEMPLATE_TYPE)) {
      return this.#dialogService
        .setSize('md')
        .open(TemplateTypeSelectionModalComponent)
        .closed.pipe(
          take(1),
          filter((category): category is TemplateCategory => !!category),
          tap((category: TemplateCategory) => this.#openUploadDialog({ category })),
        )
        .subscribe();
    } else {
      return this.#openUploadDialog();
    }
  }

  #openUploadDialog(data?: { category?: TemplateCategory }) {
    const aiBuilderAvailable = this.#featureService.isEnabled(featureFlag.TMP_AI_TEMPLATE_BUILDER) && this.#hasHrPlus();

    if (!aiBuilderAvailable) {
      return this.#openManualDialog(data);
    }

    return this.#dialogService.setSize('md').open(AiTemplateBuilderModalComponent, { data });
  }

  #openManualDialog(data?: { category?: TemplateCategory }) {
    return this.#dialogService.setSize('fullscreen').open(UploadTemplateContractModalComponent, { data });
  }
}
